import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

const FlightBookingContext = createContext();

export function FlightBookingProvider({ children }) {
  // Tracks the currently active option (0, 1, or 2)
  const [activeOption, setActiveOption] = useState(0);
  // Tracks if each option is complete
  const [optionStatus, setOptionStatus] = useState([false, false, false]);
  const [flightInfo, setFlightInfo] = useState(null);

  // Functions to navigate between options
  const goNext = useCallback(
    () => setActiveOption(prev => Math.min(prev + 1, 2)),
    [],
  );
  const goPrev = useCallback(
    () => setActiveOption(prev => Math.max(prev - 1, 0)),
    [],
  );

  const setOptionComplete = useCallback((index, value = true) => {
    setOptionStatus(prev => {
      const next = [...prev];
      next[index] = !!value;
      return next;
    });
  }, []);

  // Marks an option as complete and moves to the next one
  const markOptionComplete = useCallback(index => {
    setOptionStatus(prev => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  }, []);

  // Context value to be provided to children components
  const contextValue = useMemo(
    () => ({
      activeOption,
      setActiveOption,
      optionStatus,
      markOptionComplete,
      goNext,
      goPrev,
      flightInfo,
      setFlightInfo,
      setOptionComplete,
    }),
    [
      activeOption,
      optionStatus,
      goPrev,
      flightInfo,
      setFlightInfo,
      markOptionComplete,
      goNext,
      setOptionComplete,
    ],
  );

  return (
    <FlightBookingContext.Provider value={contextValue}>
      {children}
    </FlightBookingContext.Provider>
  );
}

export const useFlightBooking = () => {
  const ctx = useContext(FlightBookingContext);
  if (!ctx) {
    throw new Error(
      'useFlightBooking must be used within FlightBookingProvider',
    );
  }
  return ctx;
};
